type DownloadDocumentType = "receipt" | "invoice";

interface FetchProtectedFileParams {
  accessToken: string;
  streamUrl: string;
}

const LINE_IN_APP_BROWSER_RE = /Line\//i;
const MOBILE_BROWSER_RE = /iPhone|iPad|iPod|Android/i;

function isLineInAppBrowser() {
  return LINE_IN_APP_BROWSER_RE.test(window.navigator.userAgent);
}

function isMobileBrowser() {
  return MOBILE_BROWSER_RE.test(window.navigator.userAgent);
}

export function shouldUseLineExternalBrowserHandoff() {
  return isLineInAppBrowser() && isMobileBrowser();
}

export function buildLineExternalBrowserHandoffUrl(
  params: {
    accessToken: string;
    downloadType: DownloadDocumentType;
    fileName: string;
    streamUrl: string;
  }
) {
  const nextUrl = new URL("/document-viewer", window.location.origin);

  nextUrl.searchParams.set("_t", params.accessToken);
  nextUrl.searchParams.set("_dl", params.downloadType);
  nextUrl.searchParams.set("_name", params.fileName);
  nextUrl.searchParams.set("_url", params.streamUrl);
  nextUrl.searchParams.set("openExternalBrowser", "1");

  return nextUrl.toString();
}

export function handoffDownloadToExternalBrowser(
  params: {
    accessToken: string;
    downloadType: DownloadDocumentType;
    fileName: string;
    streamUrl: string;
  }
) {
  const targetUrl = buildLineExternalBrowserHandoffUrl(params);
  const liff = (window as Window & { liff?: { openWindow?: (args: { url: string; external: boolean }) => void } }).liff;

  if (liff?.openWindow) {
    liff.openWindow({
      url: targetUrl,
      external: true,
    });
    return;
  }

  const anchor = document.createElement("a");
  anchor.href = targetUrl;
  anchor.target = "_blank";
  anchor.rel = "noopener noreferrer";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  window.setTimeout(() => {
    if (document.visibilityState === "visible") {
      window.location.assign(targetUrl);
    }
  }, 250);
}

export async function fetchProtectedFile({
  accessToken,
  streamUrl,
}: FetchProtectedFileParams) {
  const response = await fetch(streamUrl, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Download failed with status ${response.status}`);
  }

  return response.blob();
}

export function openBlobFile(blob: Blob, fileName?: string) {
  const objectUrl = window.URL.createObjectURL(blob);
  const cleanup = () => window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
  const link = document.createElement("a");
  link.href = objectUrl;
  if (fileName) {
    link.download = fileName;
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  cleanup();
  return objectUrl;
}

export function openBlobInline(blob: Blob) {
  const objectUrl = window.URL.createObjectURL(blob);
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
  window.location.replace(objectUrl);
  return objectUrl;
}
